export interface IStoredGuestVote {
    pollId: string;
    choiceIds: string[];
}

const STORAGE_KEY = "uchoices";

const normalizeVote = (vote: any): IStoredGuestVote | null => {
    if (!vote || typeof vote !== "object") {
        return null;
    }

    const pollId = typeof vote.pollId === "string" ? vote.pollId : "";
    if (!pollId) {
        return null;
    }

    const rawChoiceIds: unknown[] = Array.isArray(vote.choiceIds)
        ? vote.choiceIds
        : typeof vote.choiceId === "string" && vote.choiceId
        ? [vote.choiceId]
        : [];

    const filteredChoiceIds: string[] = rawChoiceIds.filter(
        (choiceId: unknown): choiceId is string =>
            typeof choiceId === "string" && choiceId.length > 0
    );

    const choiceIds = Array.from(new Set(filteredChoiceIds));

    if (choiceIds.length === 0) {
        return null;
    }

    return { pollId, choiceIds };
};

/**
 * Read guest votes from localStorage
 * Used to persist votes across page refreshes (NPS + standard polls)
 * @returns Array of stored guest votes
 */
export const readGuestVotes = (): IStoredGuestVote[] => {
    try {
        const storedVotes = localStorage.getItem(STORAGE_KEY);
        if (!storedVotes) {
            return [];
        }

        const parsed = JSON.parse(storedVotes);
        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed
            .map((vote) => normalizeVote(vote))
            .filter((vote): vote is IStoredGuestVote => Boolean(vote));
    } catch {
        return [];
    }
};

/**
 * Save guest votes to localStorage
 * Used to persist votes across page refreshes (NPS + standard polls)
 * @param votes - Array of votes to persist
 */
export const persistGuestVotes = (votes: IStoredGuestVote[]): void => {
    const sanitized = votes
        .map((vote) => normalizeVote(vote))
        .filter((vote): vote is IStoredGuestVote => Boolean(vote));

    if (sanitized.length > 0) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitized));
    } else {
        localStorage.removeItem(STORAGE_KEY);
    }
};

/**
 * Extract poll IDs from the cookie store
 * Used by Choice.component for undo/cookie management (legacy tracking)
 * NOT essential for the two current NPS fixes (poll_type + show last choice)
 * TODO: Move to centralized vote handler in future ticket
 * @param cookieStore - Cookie object from react-cookie useCookies hook
 * @returns Array of poll IDs from the "tb" cookie
 */
export const getPollIdsFromCookies = (
    cookieStore: Record<string, string | undefined> | undefined
): string[] => {
    const tbCookie = cookieStore?.tb;
    return tbCookie ? tbCookie.split(";").filter(Boolean) : [];
};

/**
 * Get the voted choice ID for a specific poll from guest votes
 * Generic utility usable by any poll type (NPS, Standard, MultiVoting)
 * @param pollId - ID of the poll to find the vote for
 * @returns First choice ID that was voted, or null if not found
 */
export const getGuestVoteForPoll = (pollId: string): string | null => {
    const votes = readGuestVotes();
    const pollVote = votes.find((vote) => vote.pollId === pollId);
    return pollVote?.choiceIds[0] || null;
};

/**
 * Remove a specific poll's vote from localStorage
 * @param pollId - ID of the poll to remove the vote for
 */
export const removeVoteFromStorage = (pollId: string): void => {
    try {
        const existingVotes = readGuestVotes();
        const updatedVotes = existingVotes.filter(
            (vote) => vote.pollId !== pollId
        );
        persistGuestVotes(updatedVotes);
    } catch (err) {
        console.error(
            `[guestVotes] Failed to remove vote for poll ${pollId}:`,
            err
        );
        throw err;
    }
};
